import * as Sentry from '@sentry/react-native'
import { useEffect } from 'react'
import { usePlaidLink as usePlaidLinkLibrary } from 'react-plaid-link'
import { useLogError } from '@/src/common/hooks/useLogError'
import { triggerSegmentAction, triggerSegmentPlaidLinkEvent } from '@/src/services/segment'
import type { UsePlaidLink } from '@/src/common/hooks/usePlaidLink'
import { z } from 'zod/v4'
import { usePlaidLinkBase } from '@/src/common/hooks/usePlaidLink/usePlaidLinkBase'

const PlaidLinkLibraryOpen = z.function()

export const usePlaidLink: UsePlaidLink = (...args) => {
  const { token, onExit, onSuccess, openPlaidLink, loading } = usePlaidLinkBase(...args)

  const { open, ready } = usePlaidLinkLibrary({
    token,
    onExit(error, metadata) {
      if (error?.error_code) {
        onExit(error.error_code, error, metadata)
      }
    },
    onEvent(action, data) {
      void triggerSegmentPlaidLinkEvent({ component: 'Plaid Link Event', action })
      Sentry.addBreadcrumb({ category: 'plaid.event', message: action, level: 'info', data })
    },
    onSuccess(publicToken, metadata) {
      const { institution } = metadata
      onSuccess(
        {
          publicToken,
          metadata: {
            ...metadata,
            institution: institution
              ? { ...institution, id: institution.institution_id, name: institution.name }
              : undefined,
          },
        },
        metadata,
      )
    },
  })

  const logError = useLogError()

  useEffect(() => {
    ready &&
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
      Promise.try(() => PlaidLinkLibraryOpen.implement(open as any)()).catch((error: unknown) => {
        void triggerSegmentAction({
          component: 'Plaid Link Event',
          action: 'User Encountered an Error During Plaid Link',
        })
        logError(error)
        console.error(error)
      })
  }, [ready, open, logError])

  return { openPlaidLink, loading } as const
}
